import { jsx as _jsx } from "react/jsx-runtime";
import { LocationType } from '@aviv-search/types';
import { useApis } from '@search/core/hooks/useApis';
import { createContext, useContext, useState } from 'react';
import { useBrandConfiguration } from '../brands/brandConfiguration';
import { fetchHierarchies } from '../services/fetchHierarchies/fetchHierarchies';
const AdsContext = createContext({
    brand: 'immonet',
    consent: false,
    gptReady: false,
    adSenseReady: false,
    setGptReady: () => { },
    setAdSenseReady: () => { },
    adsSearchModel: undefined,
    searchModel: undefined,
    geoHierarchy: undefined,
    updateSearchModel: () => Promise.resolve(),
    updateAdsSearchModel: () => { },
    adsEnabled: true,
    adSlots: [],
    adSense: {},
    headerBiddingScriptUrl: '',
    targeting: {
        fetchHierarchy: false,
        getTargeting: () => [],
    },
    gptScriptUrl: '',
    googleTagManager: {},
});
export const useAdsContext = () => useContext(AdsContext);
export const useBrand = () => useAdsContext().brand;
export const useAdSlots = () => useAdsContext().adSlots;
export const useConsent = () => useAdsContext().consent;
export const useGptReady = () => useAdsContext().gptReady;
export const useAdSenseReady = () => useAdsContext().adSenseReady;
export const useGptScriptUrl = () => useAdsContext().gptScriptUrl;
export const useAdSense = () => useAdsContext().adSense;
export const useSearchModel = () => useAdsContext().searchModel;
export const useGeoHierdarchy = () => useAdsContext().geoHierarchy;
export const AdsProvider = ({ children, consent, brand, initialSearchModel, initialAdsSearchModel, }) => {
    const { bffUri } = useApis();
    const [innerGptReady, setGptReady] = useState(false);
    const [innerAdSenseReady, setAdSenseReady] = useState(false);
    const [innerAdsSearchModel, setAdsSearchModel] = useState(initialAdsSearchModel);
    const [innerSearchModel, setSearchModel] = useState(initialSearchModel);
    const [innerGeoHierarchy, setGeoHierarchy] = useState(undefined);
    const { brand: contextBrand, adSlots: contextAdSlots, adSense: contextAdSense, headerBiddingScriptUrl: contextHeaderBiddingScriptUrl, targeting: contextTargeting, gptScriptUrl: contextGptScriptUrl, googleTagManager: contextGoogleTagManager, } = useAdsContext();
    // Now read from the brand configuration for the current brand
    const { adSlots, adSense, headerBiddingScriptUrl, gptScriptUrl, targeting, googleTagManager } = useBrandConfiguration({ brand });
    const updatedContext = {
        brand: brand ?? contextBrand,
        consent,
        gptReady: innerGptReady,
        adSenseReady: innerAdSenseReady,
        setGptReady: () => setGptReady(true),
        setAdSenseReady: () => setAdSenseReady(true),
        adsSearchModel: innerAdsSearchModel,
        searchModel: innerSearchModel,
        updateAdsSearchModel: (newAdsSearchModel) => setAdsSearchModel(newAdsSearchModel),
        updateSearchModel: async (newSearchModel) => {
            const { fetchHierarchy } = targeting;
            const previousFirstLocationId = innerSearchModel?.locations[0]?.type === LocationType.COMMUTE
                ? innerSearchModel?.locations[0]?.placeIds[0]
                : innerSearchModel?.locations[0]?.placeId;
            setSearchModel(newSearchModel);
            const newFirstLocationId = newSearchModel?.locations[0]?.type === LocationType.COMMUTE
                ? newSearchModel?.locations[0]?.placeIds[0]
                : newSearchModel?.locations[0]?.placeId;
            // Updating the Search Model should also keep the hierarchy up to date
            // if the location context changed or the hierarchy is not fetched yet
            if (fetchHierarchy &&
                newFirstLocationId &&
                (previousFirstLocationId !== newFirstLocationId || !innerGeoHierarchy)) {
                const response = await fetchHierarchies({ placesIds: [newFirstLocationId], bffUri });
                const newGeoHierarchy = response?.[newFirstLocationId];
                if (newGeoHierarchy) {
                    setGeoHierarchy(newGeoHierarchy);
                }
            }
        },
        geoHierarchy: innerGeoHierarchy,
        adsEnabled: true,
        adSlots: adSlots || contextAdSlots,
        adSense: adSense || contextAdSense || {},
        headerBiddingScriptUrl: headerBiddingScriptUrl || contextHeaderBiddingScriptUrl,
        targeting: targeting || contextTargeting,
        gptScriptUrl: gptScriptUrl || contextGptScriptUrl,
        googleTagManager: googleTagManager || contextGoogleTagManager,
    };
    return _jsx(AdsContext.Provider, { value: updatedContext, children: children });
};
